import type { FC } from 'react';
import { useCallback } from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  useCreativeTextsDetails,
} from 'features/cabinet/facebook/api';
import CreativeTextForm from 'features/cabinet/facebook/components/CreativeTextForm';
import CreativeTextsFormSkeleton from 'features/cabinet/facebook/components/CreativeTextsFormSkeleton';
import { SuspenseBoundary } from 'components/common';
import { cabinetDatadogSources } from 'lib/datadog';

interface EditLocTextModalProps {
  textId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  autoAddLocalizedVersion?: boolean;
}

interface EditLocTextModalContentProps {
  textId: number;
  onClose: () => void;
  onSaved: () => void;
  autoAddLocalizedVersion?: boolean;
}

const EditLocTextModalContent: FC<EditLocTextModalContentProps> = ({
  textId,
  onClose,
  onSaved,
  autoAddLocalizedVersion = false,
}) => {
  const { data: creativeText } = useCreativeTextsDetails({
    params: { textId: String(textId) },
  });

  const handleUpdateSuccess = useCallback(() => {
    onSaved();
    onClose();
  }, [onClose, onSaved]);

  return (
    <CreativeTextForm
      creativeText={creativeText}
      onUpdateSuccess={handleUpdateSuccess}
      autoAddLocalizedVersion={autoAddLocalizedVersion}
      titleForm={(
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography variant="h4">Edit text settings</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      )}
      sx={{ maxWidth: '100%' }}
    />
  );
};

const EditLocTextModal: FC<EditLocTextModalProps> = ({
  textId,
  isOpen,
  onClose,
  onSaved,
  autoAddLocalizedVersion = false,
}) => {
  const handleDialogClose = () => {
    onClose();
    onSaved();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={handleDialogClose}
      PaperProps={{
        sx: { maxWidth: 920, minHeight: 156, width: '90%', px: 1, pb: 1.5 },
      }}
    >
      <DialogContent sx={{ width: '100%' }}>
        {textId ? (
          <SuspenseBoundary
            fallback={<CreativeTextsFormSkeleton />}
            datadogSource={cabinetDatadogSources.detailsQuery('facebook', 'creative_text')}
          >
            <EditLocTextModalContent
              textId={textId}
              onClose={onClose}
              onSaved={onSaved}
              autoAddLocalizedVersion={autoAddLocalizedVersion}
            />
          </SuspenseBoundary>
        ) : (
          <CreativeTextForm
            autoAddLocalizedVersion={autoAddLocalizedVersion}
            onSuccess={() => {
              onSaved();
              onClose();
            }}
            titleForm={(
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography variant="h4">Add text settings</Typography>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Box>
            )}
            sx={{ maxWidth: '100%' }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditLocTextModal;
